/**
 * FornecedorCombobox - Combobox de fornecedor com busca por digitação
 * e cadastro rápido inline.
 *
 * Props:
 *   - value: string (id do fornecedor selecionado, ou '' / null)
 *   - onChange: (id, fornecedorObj) => void
 *   - fornecedores: array
 *   - onFornecedoresUpdate: () => void  (chama após criar novo, para recarregar a lista)
 *   - placeholder, disabled, testIdPrefix
 */
import React, { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, Plus, Search } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { cn } from '../lib/utils';
import api from '../lib/axios';
import { toast } from 'sonner';

const normalize = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const FornecedorCombobox = ({
  value,
  onChange,
  fornecedores = [],
  onFornecedoresUpdate,
  placeholder = 'Selecionar fornecedor...',
  disabled = false,
  testIdPrefix = 'fornecedor',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [showCadastroRapido, setShowCadastroRapido] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [novoFornecedor, setNovoFornecedor] = useState({
    nome: '',
    cnpj_cpf: '',
    telefone: '',
    email: '',
    cidade: '',
  });

  const selecionado = useMemo(
    () => fornecedores.find((f) => f.id === value),
    [fornecedores, value]
  );

  const filtrados = useMemo(() => {
    if (!query.trim()) return fornecedores;
    const q = normalize(query);
    return fornecedores.filter(
      (f) =>
        normalize(f.nome).includes(q) ||
        normalize(f.cnpj_cpf).includes(q) ||
        normalize(f.email).includes(q) ||
        normalize(f.cidade).includes(q)
    );
  }, [fornecedores, query]);

  const podeCriar = query.trim().length >= 2 &&
    !fornecedores.some((f) => normalize(f.nome) === normalize(query));

  const handleSelect = (forn) => {
    onChange(forn.id, forn);
    setOpen(false);
    setQuery('');
  };

  const handleClear = () => {
    onChange('', null);
    setOpen(false);
    setQuery('');
  };

  const abrirCadastroRapido = () => {
    setNovoFornecedor({
      nome: query.trim(),
      cnpj_cpf: '',
      telefone: '',
      email: '',
      cidade: '',
    });
    setOpen(false);
    setShowCadastroRapido(true);
  };

  const handleCadastrarRapido = async () => {
    if (!novoFornecedor.nome.trim()) {
      toast.error('Nome do fornecedor é obrigatório');
      return;
    }
    setSalvando(true);
    try {
      const res = await api.post('/fornecedores', {
        nome: novoFornecedor.nome.trim(),
        cnpj_cpf: novoFornecedor.cnpj_cpf || '',
        telefone: novoFornecedor.telefone || '',
        email: novoFornecedor.email || '',
        cidade: novoFornecedor.cidade || '',
        ativo: true,
      });
      const novo = res.data;
      toast.success(`Fornecedor "${novo.nome}" cadastrado e selecionado`);
      // Atualizar lista global e selecionar
      if (onFornecedoresUpdate) await onFornecedoresUpdate();
      onChange(novo.id, novo);
      setShowCadastroRapido(false);
      setQuery('');
    } catch (err) {
      console.error('Erro ao cadastrar fornecedor:', err);
      toast.error(err?.response?.data?.detail || 'Erro ao cadastrar fornecedor');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(''); }}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between font-normal"
            data-testid={`${testIdPrefix}-trigger`}
          >
            <span className={cn('truncate', !selecionado && 'text-gray-500')}>
              {selecionado ? selecionado.nome : placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0" style={{ width: 'var(--radix-popover-trigger-width)' }} align="start">
          <div className="flex items-center border-b px-3" cmdk-input-wrapper="">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              autoFocus
              placeholder="Digite para buscar..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
              data-testid={`${testIdPrefix}-search`}
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {value && (
              <button
                type="button"
                onClick={handleClear}
                className="w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 italic"
                data-testid={`${testIdPrefix}-clear`}
              >
                Limpar seleção
              </button>
            )}
            {filtrados.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-gray-500">
                Nenhum fornecedor encontrado
              </div>
            ) : (
              filtrados.map((forn) => (
                <button
                  type="button"
                  key={forn.id}
                  onClick={() => handleSelect(forn)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-100 flex items-center gap-2 text-sm"
                  data-testid={`${testIdPrefix}-option-${forn.id}`}
                >
                  <Check
                    className={cn('h-4 w-4 shrink-0', value === forn.id ? 'opacity-100 text-green-600' : 'opacity-0')}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{forn.nome}</div>
                    {(forn.cnpj_cpf || forn.cidade) && (
                      <div className="text-xs text-gray-500 truncate">
                        {forn.cnpj_cpf}
                        {forn.cnpj_cpf && forn.cidade ? ' · ' : ''}
                        {forn.cidade}
                      </div>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
          {podeCriar && (
            <div className="border-t p-1">
              <button
                type="button"
                onClick={abrirCadastroRapido}
                className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-blue-50 text-blue-700 flex items-center gap-2"
                data-testid={`${testIdPrefix}-create`}
              >
                <Plus className="h-4 w-4" />
                <span>Cadastrar fornecedor "<strong>{query.trim()}</strong>"</span>
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* Modal de cadastro rápido */}
      <Dialog open={showCadastroRapido} onOpenChange={(o) => !salvando && setShowCadastroRapido(o)}>
        <DialogContent className="max-w-md" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Cadastro Rápido de Fornecedor</DialogTitle>
            <DialogDescription>
              Preencha os dados básicos. Você pode complementar o cadastro depois em Cadastros → Fornecedores.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Razão Social / Nome *</Label>
              <Input
                autoFocus
                value={novoFornecedor.nome}
                onChange={(e) => setNovoFornecedor((p) => ({ ...p, nome: e.target.value }))}
                placeholder="Ex: Yamaha Motor do Brasil LTDA"
                data-testid={`${testIdPrefix}-quickform-nome`}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">CNPJ / CPF</Label>
                <Input
                  value={novoFornecedor.cnpj_cpf}
                  onChange={(e) => setNovoFornecedor((p) => ({ ...p, cnpj_cpf: e.target.value }))}
                  placeholder="00.000.000/0000-00"
                  data-testid={`${testIdPrefix}-quickform-cnpj`}
                />
              </div>
              <div>
                <Label className="text-xs">Telefone</Label>
                <Input
                  value={novoFornecedor.telefone}
                  onChange={(e) => setNovoFornecedor((p) => ({ ...p, telefone: e.target.value }))}
                  placeholder="(00) 00000-0000"
                  data-testid={`${testIdPrefix}-quickform-telefone`}
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input
                type="email"
                value={novoFornecedor.email}
                onChange={(e) => setNovoFornecedor((p) => ({ ...p, email: e.target.value }))}
                placeholder="contato@fornecedor.com.br"
                data-testid={`${testIdPrefix}-quickform-email`}
              />
            </div>
            <div>
              <Label className="text-xs">Cidade</Label>
              <Input
                value={novoFornecedor.cidade}
                onChange={(e) => setNovoFornecedor((p) => ({ ...p, cidade: e.target.value }))}
                placeholder="Ex: São Paulo - SP"
                data-testid={`${testIdPrefix}-quickform-cidade`}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowCadastroRapido(false)} disabled={salvando}>
              Cancelar
            </Button>
            <Button type="button" onClick={handleCadastrarRapido} disabled={salvando} data-testid={`${testIdPrefix}-quickform-save`}>
              {salvando ? 'Salvando...' : 'Cadastrar e selecionar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FornecedorCombobox;
